import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import HrbTranslate from '../../../../../components/language/HrbTranslate';
import Button from '../../../../../components/library/buttons/Button';
import BlockSignalLink from '../../../../../components/library/common/BlockSignalLink';
import DateTime from '../../../../../components/library/common/DateTime';
import EmdashText from '../../../../../components/library/common/EmdashText';
import LoadingTextValue from '../../../../../components/library/layout/LoadingTextValue';
import VerticalNameChildren from '../../../../../components/library/layout/VerticalNameChildren';
import TaskErrorText from '../../../../../components/library/workflow-tasks/TaskErrorText';
import WorkflowTask from '../../../../../components/library/workflow-tasks/WorkflowTask';
import WorkflowTaskDescriptionWithHelp from '../../../../../components/library/workflow-tasks/WorkflowTaskDescriptionWithHelp';
import ReturnFiledApi from '../../../../../services/api/returnFiledApi';
import TicketActions from '../../../../../store/ticketRedux';
import { TaskStatus } from '../../../services/TaskStatus';
import { WorkflowTasks } from '../../../services/WorkflowTasks';
import { useInterviewInfo } from '../../hooks/useInterviewInfoHook';
import { useTaskStatus } from '../../hooks/useTaskStatusHook';
import { useWorkflowState } from '../../hooks/useWorkflowStateHook';
import '../Workflow.scss';
import { useTicketState } from '../../hooks/useTicketStateHook';
import { trackEvent, HeapEvents } from '../../../../../services/util/AnalyticsUtil';
import DoxExplorer from '../../dox/Explorer/doxExplorer';
import DoxUploader from '../../dox/Uploader/doxUploader';

const FileReturnTask = (props) => {

    const dispatch = useDispatch()
    const ticketIsOpen = useTicketState(props.ticket);

    // basic interview information
    const [returnType, , province, primaryName, spouseName] = useInterviewInfo(props.ticket);
    const stateTasks = useWorkflowState(props.ticket)

    // tasks to check for in this tile
    const [tasks, setTasks] = useState([WorkflowTasks.RETURN_FILED]);    

    const [baseStatus, taskCompletions] = useTaskStatus(props.ticket, tasks);
    const [readyToFile, setReadyToFile] = useState(false)
    const [explorerShowing, setExplorerShowing] = useState(false)
    const [uploaderOpen, setUploaderOpen] = useState(false)
    const [hasUploaderError, setHasUploaderError] = useState(false)
    const [hasCompletionError, setHasCompletionError] = useState(false)
    const [completing, setCompleting] = useState('')

    // for overriding status on panel
    const [status, setStatus] = useState(TaskStatus.NOT_STARTED);

    // track individual statii for each filer
    const [primaryStatus, setPrimaryStatus] = useState('')
    const [spouseStatus, setSpouseStatus] = useState('')

    // task completion data for each filer
    const [primaryTimestamp, setPrimaryTimestamp] = useState({})
    const [spouseTimestamp, setSpouseTimestamp] = useState({})

    const renderTimestamp = (timestamp, complete) => {
        if (complete) {
            return timestamp ?
                <p className='p1 medium-weight'><DateTime dateAndTime value={timestamp} /></p> :
                <HrbTranslate text='ConfirmIdTask.ErrorTimestamp' params={{ BlockSignal: <BlockSignalLink /> }} className='p2 error' />
        } else {
            return <p className='p1 medium-weight'><EmdashText /></p>
        }
    }

    const completeReturnFiledTaskForFiler = async (isPrimary) => {
        try {
            let completingFor = isPrimary ? 'primary' : 'spouse'
            setCompleting(completingFor)
            if (props.ticket) {
                var response = await ReturnFiledApi.create().completeReturnFiledTask(props.ticket.id, completingFor)
                if (response && response.ok) {
                    let success = response.data.status === TaskStatus.COMPLETE
                    dispatch(TicketActions.getTicket(props.ticket.id))
                    setHasCompletionError(success ? '' : (isPrimary ? 'primary' : 'spouse'))
                    trackEvent(isPrimary ? HeapEvents.COMPLETE_RETURN_PRIMARY : HeapEvents.COMPLETE_RETURN_SPOUSE, props.ticket.id)
                } else {
                    setHasCompletionError('other')
                }
                
            }
        } catch (ex) {
            setHasCompletionError('other')
        } finally {
            setCompleting('')
        }
    }

    const openUploader = () => {
        setUploaderOpen(true)
    }

    const onUploadsComplete = async () => {
        setUploaderOpen(false);
    }

    const closeUploader = () => {
        setUploaderOpen(false)
    }

    useEffect(() => {
        setTasks(returnType === 'coupled' ?
            [WorkflowTasks.RETURN_FILED, WorkflowTasks.RETURN_FILED_SPOUSE] :
            [WorkflowTasks.RETURN_FILED])
    }, [returnType])

    useEffect(() => {
        // are we currently ready to file?
        let ready = stateTasks.find(t => t.identifier === WorkflowTasks.RETURN_FILED)
        if (Boolean(ready) !== readyToFile) {
            setReadyToFile(Boolean(ready))
        }
    }, [stateTasks])

    useEffect(() => {
        let tcs = taskCompletions || []

        let primary = tcs.find(x => x.status !== TaskStatus.INVALID && x.workflowTaskId === WorkflowTasks.RETURN_FILED)
        if (primary) {
            setPrimaryTimestamp(primary.lastModified)
            setPrimaryStatus(primary.status || '')
        }

        let spouse = tcs.find(x => x.status !== TaskStatus.INVALID && x.workflowTaskId === WorkflowTasks.RETURN_FILED_SPOUSE)
        if (spouse) {
            setSpouseTimestamp(spouse.lastModified)
            setSpouseStatus(spouse.status || '')
        }
    }, [taskCompletions])

    useEffect(() => {
        if (hasCompletionError ||
            (primaryStatus === TaskStatus.COMPLETE && !primaryTimestamp) ||
            (spouseStatus === TaskStatus.COMPLETE && !spouseTimestamp)) {
            setStatus(TaskStatus.ERROR)
        } else if ((readyToFile && baseStatus === TaskStatus.NOT_STARTED) || baseStatus === TaskStatus.IN_PROGRESS) {
            setStatus(TaskStatus.READY_TO_FILE)
        } else {
            setStatus(baseStatus)
        }
    }, [baseStatus, primaryStatus, spouseStatus, readyToFile, hasCompletionError])

    return (
        <WorkflowTask status={status} taskId={WorkflowTasks.RETURN_FILED}>
            <div className='columns'>
                <WorkflowTaskDescriptionWithHelp
                    helpKey='0ProSupport/VDOPro/FileReturn.htm'
                    descriptions={['FileReturnTask.ReviewDocsTitle']}
                    classNames='p1 medium-weight' />
            </div>
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-8'>
                    <HrbTranslate text={province === 'QC' ? 'FileReturnTask.ReviewDocsDescQC' : 'FileReturnTask.ReviewDocsDesc'} className='p2' />
                </div>
            </div>
            <div className='columns'>
                <div className='column is-offset-1 is-10'>
                    {props.ticket &&
                        <Fragment>                                                        
                            {hasUploaderError &&
                                <TaskErrorText errorKey='PrepareTask.ErrorUploader' />
                            }
                            <div className='step-center-container'>
                                <Button
                                    elementId='prepare-manage-btn'
                                    labelKey='DocUploadTask.ViewManage'
                                    buttonStyles='secondary'
                                    onClick={() => setExplorerShowing(true)}
                                    disabled={baseStatus === TaskStatus.COMPLETE || !ticketIsOpen}
                                />
                                <Button
                                    elementId='upload-extra-docs-btn'
                                    labelKey='FileReturnTask.UploadDocs'
                                    buttonStyles='secondary'
                                    onClick={() => openUploader()}
                                    disabled={!readyToFile && ticketIsOpen}
                                />
                            </div>
                        </Fragment>
                    }
                </div>
            </div>
            <div className='columns divider'>
                <div className='column is-offset-1 is-10'>
                    <div className='prep-border' />
                </div>
            </div>
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-10'>
                    <p className='p1 medium-weight'><HrbTranslate text='FileReturnTask.ConfirmTitle' /></p>
                    <p className='p2 step-content'><HrbTranslate text='FileReturnTask.ConfirmDesc' /></p>
                    <p className='p2 step-content'><HrbTranslate text='FileReturnTask.ConfirmDesc2' /></p>
                </div>
            </div>
            <div className='columns'>
                <div className={classnames('column is-offset-1', { 'is-5': returnType === 'coupled', 'is-10': returnType !== 'coupled' })}>
                    <h4><LoadingTextValue loading={!props.ticket} value={primaryName} /></h4>
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-5'>
                        <h4><LoadingTextValue loading={!props.ticket} value={spouseName} /></h4>
                    </div>
                }
            </div>
            <div className='columns'>
                <div className={classnames('column is-offset-1', { 'is-5': returnType === 'coupled', 'is-10': returnType !== 'coupled' })}>
                    <VerticalNameChildren
                        textKey="FileReturnTask.CompletedOn"
                        loading={!props.ticket}>
                        {renderTimestamp(primaryTimestamp, primaryStatus === TaskStatus.COMPLETE)}
                    </VerticalNameChildren>
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-5'>
                        <VerticalNameChildren
                            textKey="FileReturnTask.CompletedOn"
                            loading={!props.ticket}>
                            {renderTimestamp(spouseTimestamp, spouseStatus === TaskStatus.COMPLETE)}
                        </VerticalNameChildren>
                    </div>
                }
            </div>
            <div className='columns'>
                <div className={classnames('column is-offset-1', { 'is-5': returnType === 'coupled', 'is-10': returnType !== 'coupled' })}>
                    <Button
                        elementId='btn-return-filed-primary'
                        labelKey='FileReturnTask.ReturnCompleted'
                        buttonStyles='primary'
                        disabled={!readyToFile || primaryStatus === TaskStatus.COMPLETE || !ticketIsOpen}
                        onClick={() => completeReturnFiledTaskForFiler(true)}
                        loading={completing === 'primary'}
                    />
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-5'>
                        <Button
                            elementId='btn-return-filed-spouse'
                            labelKey='FileReturnTask.ReturnCompleted'
                            buttonStyles='primary'
                            disabled={!readyToFile || spouseStatus === TaskStatus.COMPLETE || !ticketIsOpen}
                            onClick={() => completeReturnFiledTaskForFiler(false)}
                            loading={completing === 'spouse'}
                        />
                    </div>
                }
            </div>
            {hasCompletionError && (hasCompletionError === 'primary' || hasCompletionError === 'spouse') &&
                <div className='columns'>
                    <div className={classnames('column is-offset-1', { 'is-5': returnType === 'coupled', 'is-10': returnType !== 'coupled' })}>
                        {hasCompletionError === 'primary' &&
                            <TaskErrorText errorKey='FileReturnTask.ErrorSubmit' />
                        }
                    </div>
                    {returnType === 'coupled' &&
                        <div className='column is-5'>
                            {hasCompletionError === 'spouse' &&
                                <TaskErrorText errorKey='FileReturnTask.ErrorSubmit' />
                            }
                        </div>
                    }
                </div>
            }
            {hasCompletionError && hasCompletionError === 'other' &&
                <div className='columns mt-2'>
                    <div className='column is-offset-1 is-10'>
                        <TaskErrorText errorKey='FileReturnTask.ErrorFinalize' />
                    </div>
                </div>
            }
            <DoxExplorer ticket={props.ticket} show={explorerShowing}
                onClose={() => setExplorerShowing(false)}
            />
            <DoxUploader ticket={props.ticket} show={uploaderOpen}
                onUploadsComplete={onUploadsComplete} onClose={closeUploader} layoutType={'desktop'}
            />
        </WorkflowTask>
    );
}

FileReturnTask.whyDidYouRender = false;

FileReturnTask.propTypes = {
    ticket: PropTypes.object
}

export default FileReturnTask